import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';

export type SapIssueScope = 'header' | 'position';

/**
 * A single mandatory-field problem found while validating an extraction result
 * for SAP export. `scope` distinguishes header/company issues from per-position
 * issues; `productId`/`positionNumber` allow the UI to map the issue to a row.
 */
@Entity()
export class SapFieldIssue extends AdornisEntity {
  static override _class = 'SapFieldIssue';

  static override resolveID = (value: Partial<SapFieldIssue>) =>
    value._id ?? `${value.scope ?? ''}:${value.positionNumber ?? ''}:${value.productId ?? ''}:${value.field ?? ''}`;

  @Field(() => String, { default: v => v ?? A.getGloballyUniqueID() }) _id!: string;
  @Field(() => String) scope!: SapIssueScope;
  @Field(() => String) field!: string;
  @Field(() => String) message!: string;
  @Field(() => String) positionNumber: Maybe<string>;
  @Field(() => String) productId: Maybe<string>;

  constructor(doc: PartialEntityData<SapFieldIssue>) {
    super(doc);
  }
}

/**
 * Result of validating an extraction result against the mandatory SAP fields.
 * `ok` is true exactly when there are no blocking errors.
 */
@Entity()
export class SapValidationReport extends AdornisEntity {
  static override _class = 'SapValidationReport';

  @Field(() => String, { default: v => v ?? A.getGloballyUniqueID() }) _id!: string;
  @Field(() => Boolean) ok!: boolean;
  @Field(() => [SapFieldIssue]) errors!: SapFieldIssue[];

  constructor(doc: PartialEntityData<SapValidationReport>) {
    super(doc);
  }
}
